import type { Recipe, RecipeCategory } from 'types';

export function useRecipes() {
  const result = useFetch<Recipe[]>('/api/recipes');

  // Ensure client-side cache population for offline functionality
  if (import.meta.client) {
    onMounted(async () => {
      try {
        // This client-side fetch will be intercepted by the service worker for caching
        await $fetch('/api/recipes');
      } catch (error) {
        // Silently fail - we already have SSR data
        console.debug('Client-side cache population failed:', error);
      }
    });
  }

  return result;
}

export function useRecipe(slug: string) {
  const { data: recipes, ...rest } = useRecipes();
  const recipe = computed(
    () => recipes.value?.find((r) => r.slug === slug) as Recipe,
  );
  return { data: recipe, ...rest };
}

export function filterRecipes(recipes: Recipe[], search: string): Recipe[] {
  const searches = search
    .split(' ')
    .filter((s) => !!s)
    .map((s) => s.toLowerCase());

  if (searches.length === 0) return recipes;

  return recipes.filter(({ title, subtitle, source, tags }) => {
    const searchStr = `
      ${title || ''}
      ${subtitle || ''}
      ${source || ''}
      ${tags && Array.isArray(tags) ? tags.join('') : ''}
    `.toLowerCase();

    return searches.every((s) => searchStr.indexOf(s) > -1); // AND search
    // return searches.some((s) => searchStr.indexOf(s) > -1) // OR search
  });
}

export function getCategories(recipes: Recipe[], search = '') {
  const filteredRecipes = filterRecipes(recipes, search);
  const categories: Record<RecipeCategory, Recipe[]> = {
    breakfast: [],
    salad: [],
    dinner: [],
    snack: [],
    side: [],
    soup: [],
    dessert: [],
    drink: [],
    other: [],
  };

  if (filteredRecipes.length) {
    filteredRecipes.forEach((recipe) => {
      const category = recipe.category as RecipeCategory;
      if (category && category in categories) {
        categories[category].push(recipe);
      } else {
        categories.other.push(recipe);
      }
    });
  }

  return Object.entries(categories).reduce<
    Partial<Record<RecipeCategory, Recipe[]>>
  >((acc, [key, value]) => {
    if (value.length) acc[key as RecipeCategory] = value;
    return acc;
  }, {});
}
